import React from 'react';
import { useDashboardOverview } from '../api/dashboard';
import { useBffClient } from '../api/client';
import { useDevices } from '../api/devices';
import type { Device } from '../types/device';

export type FirstRunStep = 'install' | 'connect' | 'verify' | 'complete';

export const WELCOME_SKIPPED_KEY = 'redbax_welcome_skipped';

export interface FirstRun {
  step: FirstRunStep;
  /** The device the flow is following, once there is one. */
  device: Device | null;
  /** True until both underlying queries have answered, so the flow can avoid flashing step 1. */
  isLoading: boolean;
}

/**
 * Where a new user has got to, derived entirely from server facts (epic #121).
 *
 * NOTHING IS STORED. The step is recomputed from "do they have a device" and "have they seen a
 * detection" on every render, which is what makes the flow resumable: close the tab mid-install, come
 * back tomorrow on a different machine, and it picks up exactly where reality is. A stored step would
 * have to be kept in sync with the thing it describes, and the two would drift the first time someone
 * removed a device.
 *
 * Completion is checked FIRST, deliberately. A user who has seen a detection is finished even if the
 * device has since gone offline — a laptop that was shut for the night must not drag someone back into
 * onboarding.
 */
export function useFirstRun(): FirstRun {
  const { data: devices, isLoading: devicesLoading } = useDevices();
  const { data: overview, isLoading: overviewLoading } = useDashboardOverview();

  return React.useMemo(
    () => ({
      ...deriveFirstRun(devices ?? [], overview?.totalDetections ?? 0),
      isLoading: devicesLoading || overviewLoading,
    }),
    [devices, overview, devicesLoading, overviewLoading],
  );
}

/**
 * The derivation itself, as a pure function.
 *
 * Separated from the hook so it can be tested as the state machine it is, against a list and a number,
 * rather than through two mocked React Query calls — the branch that matters most ("already finished,
 * device since gone offline") is one line and should not need a rendered component to assert.
 */
export function deriveFirstRun(
  devices: Device[],
  totalDetections: number,
): { step: FirstRunStep; device: Device | null } {
  // Removed and revoked devices are not progress — the same slots the device cap ignores.
  const live = devices.filter((d) => d.status !== 'Removed' && d.status !== 'Revoked');
  const online = live.find((d) => d.status === 'Online') ?? null;

  // Completion is checked FIRST, deliberately: someone who has seen a detection is finished even if
  // the device has since gone offline. A laptop shut for the night must not drag them back into
  // onboarding.
  if (totalDetections > 0)
    return { step: 'complete', device: online ?? live[0] ?? null };

  if (live.length === 0)
    return { step: 'install', device: null };

  if (!online)
    return { step: 'connect', device: live[0] };

  return { step: 'verify', device: online };
}

/**
 * Whether the user asked not to be taken through the flow.
 * <p>
 * localStorage, and therefore per browser — which is the honest limit of it. It only suppresses the
 * automatic redirect; the devices page still links back, so skipping is recoverable rather than final.
 */
export function hasSkippedWelcome(): boolean {
  try {
    return localStorage.getItem(WELCOME_SKIPPED_KEY) === 'true';
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    return false;
  }
}

export function skipWelcome() {
  try {
    localStorage.setItem(WELCOME_SKIPPED_KEY, 'true');
  } catch {
    // Not being able to remember the skip is survivable; failing the click is not.
  }
}

/**
 * The first-run milestones a browser is allowed to report (story #137). Must match
 * FunnelEventTypes.ClientReportable on the server, which refuses anything else — everything outside
 * this list is recorded from something that actually happened, so that the funnel measures the product
 * rather than what a console claims.
 */
export type FirstRunMilestone =
  | 'welcome_started'
  | 'agent_downloaded'
  | 'welcome_completed'
  | 'welcome_skipped';

/**
 * Reports a first-run milestone. Never throws and never blocks: a metric that cannot be recorded must
 * not surface as an error in the flow the person is in the middle of.
 */
export function useFirstRunReporter() {
  const client = useBffClient();

  return React.useCallback(
    (milestone: FirstRunMilestone) => {
      void client
        .post('/bff/organizations/current/funnel/milestone', { event: milestone })
        .catch(() => {});
    },
    [client],
  );
}
